import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["modal", "confirmationInput", "submitButton", "notesInput"]
  static values = {
    shouldShow: Boolean,
    submitUrl: String
  }

  connect() {
    this.modalInstance = null
    this.isModalShowing = false
    this.isInternalNavigation = false

    // Set up confirmation input listener
    this.setupConfirmationInputListener()

    // Track clicks on internal navigation links to skip confirmation
    this.handleInternalNavClick = this.handleInternalNavClick.bind(this)
    document.addEventListener('click', this.handleInternalNavClick, true) // Use capture phase

    // Reset state and clean up backdrop when modal is fully hidden.
    // Resetting on close (not open) avoids racing with user input — anything
    // the user has typed while the modal is open is preserved.
    if (this.hasModalTarget) {
      this.modalTarget.addEventListener('hidden.bs.modal', () => {
        this.isModalShowing = false
        this.cleanupModalBackdrop()
        if (this.hasConfirmationInputTarget) {
          this.confirmationInputTarget.value = ''
        }
        if (this.hasNotesInputTarget) {
          this.notesInputTarget.value = ''
        }
        if (this.hasSubmitButtonTarget) {
          this.submitButtonTarget.disabled = true
        }
      })
    }

    // Set up navigation guards if organizer is complete but not submitted
    // Don't auto-show modal on page load - only show when user tries to navigate away
    // Only use Turbo intercept, not beforeunload (beforeunload is too aggressive with Turbo)
    if (this.shouldShowValue) {
      this.setupTurboIntercept()
    }
  }

  disconnect() {
    this.removeBeforeUnload()
    this.removeTurboIntercept()
    this.disposeModal()
    this.cleanupModalBackdrop()
    document.removeEventListener('click', this.handleInternalNavClick, true)
  }

  // Track when user clicks on internal navigation (like tabs)
  handleInternalNavClick(event) {
    const target = event.target.closest('[data-internal-navigation]')
    if (target) {
      // Set a flag that this is internal navigation
      this.isInternalNavigation = true
      // Reset the flag after a short delay (after turbo:before-visit fires)
      setTimeout(() => {
        this.isInternalNavigation = false
      }, 100)
    }
  }

  showModal() {
    // Don't show if already showing
    if (this.isModalShowing) {
      return
    }

    if (this.hasModalTarget) {
      // Get or create modal instance
      this.modalInstance = bootstrap.Modal.getInstance(this.modalTarget)

      if (!this.modalInstance) {
        this.modalInstance = new bootstrap.Modal(this.modalTarget)
      }

      this.isModalShowing = true
      this.modalInstance.show()

      // Track when modal is hidden to update state
      this.modalTarget.addEventListener('hidden.bs.modal', () => {
        this.isModalShowing = false
      }, { once: true })
    }
  }

  hideModal() {
    if (this.hasModalTarget) {
      // Get existing instance or find it from Bootstrap
      let instance = this.modalInstance || window.bootstrap?.Modal?.getInstance(this.modalTarget)
      if (instance) {
        instance.hide()
        this.isModalShowing = false
      }
    }
    // Ensure backdrop is cleaned up
    this.cleanupModalBackdrop()
  }

  disposeModal() {
    if (this.modalInstance) {
      this.modalInstance.dispose()
      this.modalInstance = null
      this.isModalShowing = false
    }
    // Ensure backdrop is cleaned up
    this.cleanupModalBackdrop()
  }

  // Set up confirmation input listener
  setupConfirmationInputListener() {
    if (this.hasConfirmationInputTarget && this.hasSubmitButtonTarget) {
      this.confirmationInputTarget.addEventListener('input', (event) => {
        const inputValue = event.target.value.trim()
        this.submitButtonTarget.disabled = inputValue.toLowerCase() !== 'agree'
      })
    }
  }

  // Handle submission from modal
  async submitNow(event) {
    event.preventDefault()

    // Validate confirmation text
    if (this.hasConfirmationInputTarget) {
      const confirmationText = this.confirmationInputTarget.value.trim()
      if (confirmationText.toLowerCase() !== 'agree') {
        alert('Please type "Agree" to confirm submission.')
        return
      }
    }

    // Unified submission for both business and individual organizers
    if (!this.hasSubmitUrlValue) {
      console.error('No submit URL configured')
      alert('Configuration error: submit URL not provided')
      return
    }

    await this.directSubmit(event)
  }

  // Submit tax organizer via Turbo Stream
  async directSubmit(event) {
    const button = event.currentTarget

    // Disable button and show loading state
    button.disabled = true
    button.innerHTML = '<i class="fas fa-spinner fa-spin me-2"></i>Submitting...'

    try {
      const notes = this.hasNotesInputTarget ? this.notesInputTarget.value : null

      const response = await fetch(this.submitUrlValue, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'text/vnd.turbo-stream.html',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        },
        body: JSON.stringify({ notes: notes })
      })

      // Let Turbo process the stream response (updates progress card, flash, etc.)
      const html = await response.text()
      Turbo.renderStreamMessage(html)

      // Close the modal and scroll flash into view
      this.hideModal()
      document.getElementById('flash')?.scrollIntoView({ behavior: 'smooth', block: 'start' })

      if (response.ok) {
        // Remove navigation guards since submission is complete
        this.removeBeforeUnload()
        this.removeTurboIntercept()
      }
    } catch (error) {
      console.error('Error submitting tax organizer:', error)
      alert('An error occurred while submitting the tax organizer')
      this.hideModal()
    }
  }

  // Manually trigger the modal (for submit button clicks)
  triggerModal(event) {
    event.preventDefault()
    this.showModal()
  }

  // Setup beforeunload listener
  setupBeforeUnload() {
    this.beforeUnloadHandler = (event) => {
      // Don't show browser prompt if modal is already visible (avoid double-nag)
      if (this.isModalShowing) {
        return
      }

      // Show browser's native confirmation dialog
      event.preventDefault()
      event.returnValue = '' // Chrome requires returnValue to be set

      // The browser will show its own dialog. If the user cancels (stays on page),
      // we'll show our modal again after a short delay
      setTimeout(() => {
        // Check if user is still on the page (they cancelled the navigation)
        if (document.hasFocus()) {
          this.showModal()
        }
      }, 100)

      return ''
    }

    window.addEventListener('beforeunload', this.beforeUnloadHandler)
  }

  // Remove beforeunload listener
  removeBeforeUnload() {
    if (this.beforeUnloadHandler) {
      window.removeEventListener('beforeunload', this.beforeUnloadHandler)
    }
  }

  // Setup Turbo navigation intercept
  setupTurboIntercept() {
    this.turboBeforeVisitHandler = (event) => {
      // If modal is already showing, prevent navigation silently (avoid double-nag)
      if (this.isModalShowing) {
        event.preventDefault()
        return
      }

      // If this is internal navigation (tabs, etc.), allow it without confirmation
      if (this.isInternalNavigation) {
        return
      }

      // Navigating away from tax organizer - ask for confirmation
      if (!confirm('You have completed your tax organizer but haven\'t submitted it yet. Are you sure you want to leave without submitting?')) {
        // User wants to stay - prevent navigation
        event.preventDefault()

        // Show the completion modal again
        this.showModal()
      }
      // If user clicks OK (wants to leave), do nothing and let Turbo navigate
    }

    document.addEventListener('turbo:before-visit', this.turboBeforeVisitHandler)
  }

  // Remove Turbo navigation intercept
  removeTurboIntercept() {
    if (this.turboBeforeVisitHandler) {
      document.removeEventListener('turbo:before-visit', this.turboBeforeVisitHandler)
    }
  }

  // Clean up any leftover modal backdrops and restore body scroll
  cleanupModalBackdrop() {
    // Remove any lingering modal backdrops
    const backdrops = document.querySelectorAll('.modal-backdrop')
    backdrops.forEach(backdrop => backdrop.remove())

    // Restore body scrolling
    document.body.classList.remove('modal-open')
    document.body.style.overflow = ''
    document.body.style.paddingRight = ''
  }
}
